// First import on purpose: registers a popstate listener ahead of Turbo's
// (Turbo starts at import). See the module header; pinned by
// spec/config/turbo_history_guard_import_order_spec.rb.
import "./lib/turbo_history_guard";
import { Turbo } from "@hotwired/turbo-rails";
import { post } from "@rails/request.js";

import "./channels";
import * as bootstrap from "bootstrap";
import { showConfirmModal } from "./lib/custom_confirm";
import { showToast } from "./lib/toast";

// Replace browser-native confirm dialogs with custom gl-modal for all Turbo confirmations
Turbo.config.forms.confirm = (message, _element, _submitter) => {
  return showConfirmModal(message)
};

import "./src/add_jquery";
import { initTurboSubmitBusy } from "./lib/turbo_submit_busy";
import { initSlowVisitOverlay, createLoadingOverlayQueue, beginNavigation, settleNavigation } from "./lib/slow_visit_overlay";
import { initGlobalErrorModal, frameMissingDueToServerError } from "./lib/global_error_modal";
import { initSessionExpiryInterceptor, initSessionTimeoutWatchdog } from "./lib/session_expiry";
import { initAvatarFallback } from "./lib/avatar_fallback";
import { initModalAutofocus } from "./lib/modal_autofocus";
import { initTooltipEdgeAlign } from "./lib/tooltip_edge_align";
import { initSelectSteadyWidth } from "./lib/select_steady_width";
import { installSoftphoneFocusGuard } from "./lib/softphone_focus_guard";
import { bindCalendarLoadState, retryCalendarLoad } from "./lib/calendar_load_state";
import { loadFullCalendar } from "./lib/fullcalendar_loader";
import { initCrmBannerOffset } from "./lib/crm_banner_offset";
import "chartkick/chart.js";
import "select2/dist/js/select2.full";
import("select2").then((m) => m.default());
import select2 from "select2";

import "./controllers";

////////////////////////////////////////////////////////////////////////////////
// EARLY THEME INITIALIZATION
// Apply theme immediately to prevent flash of wrong theme
////////////////////////////////////////////////////////////////////////////////

(function initializeTheme() {
  // Force light mode on landing/public pages (no theme switching). The public
  // blog (body.gl-blog-body, layouts/blog) is a light-only marketing surface:
  // without this, a dark-OS anonymous visitor gets data-bs-theme="dark" and
  // _dark-mode.scss flips the surfaces under the blog's approved light colors
  // (#11031 — unreadable navy-on-black text).
  const isLandingPage = () => {
    const publicBodyClasses = ["landing-body", "gl-blog-body"];
    const bodyClass = publicBodyClasses.some((cls) => document.body?.classList?.contains(cls));
    const publicPaths = ["/", "/pricing", "/about", "/contact"];
    const isPublicPath = publicPaths.includes(window.location.pathname) ||
                         window.location.pathname === '/welcome' ||
                         window.location.pathname.startsWith('/welcome/');
    return bodyClass || isPublicPath;
  };

  if (isLandingPage()) {
    document.documentElement.setAttribute("data-bs-theme", "light");
    return;
  }

  const getSystemTheme = () =>
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

  const getPreference = () => {
    const stored = localStorage.getItem("themePreference");
    if (stored) return stored;
    const serverPref = document.documentElement.dataset.userThemePreference;
    if (serverPref) return serverPref;
    return "system";
  };

  const resolveTheme = (preference) => {
    if (preference === "system") return getSystemTheme();
    return preference;
  };

  const preference = getPreference();
  const theme = resolveTheme(preference);
  document.documentElement.setAttribute("data-bs-theme", theme);
})();

////////////////////////////////////////////////////////////////////////////////

document.addEventListener("turbo:render", () => {
  const wrapper = document.querySelector("#wrapper");
  const overlay = document.getElementById("loading-overlay");

  if (overlay && wrapper) {
    overlay.classList.add("hidden");
    wrapper.classList.add("loaded");
  }
});

// Re-show the overlay while a slow screen load is in flight — a Turbo visit,
// a Turbo form POST, or a native (data-turbo=false) form / link (#13805). It
// ships visible in each page's HTML but nothing re-showed it on navigation, so
// slow server responses looked like a frozen page.
initSlowVisitOverlay();
// BUTTON-8: every Turbo form submit marks its button busy (spinner, label
// unchanged, aria-busy) until the request settles.
initTurboSubmitBusy();
// The shared loader's page-facing API:
//   createQueue()      inline view scripts (lead_orders/new) borrow the overlay
//                      around AJAX calls — one show per hide per request, the
//                      shape that survives overlapping calls — so an AJAX hide
//                      can never strip the overlay from a slow load in flight.
//   beginNavigation()  code that navigates without a DOM event (form.submit(),
//                      location.assign()) announces the screen load so the goat
//                      shows after the same no-flash delay; the React
//                      impersonation helper (react/lib/impersonate.ts) is the
//                      first caller. settleNavigation() withdraws it.
// The lower-level acquireLoadingOverlay() stays a module export (Jest-covered)
// until a page genuinely needs a single borrow.
window.GoatLoadingOverlay = { createQueue: createLoadingOverlayQueue, beginNavigation, settleNavigation };

// Global branded error modal (#7095): shown for failed/timed-out in-page
// requests from both the Turbo and React worlds.
initGlobalErrorModal();

// Session-expiry: turn any Turbo 401 / login-redirect into a clean trip to the
// login page instead of the "Something went wrong" modal. Registered after the
// error modal so its suppression guard (isSessionExpiryRedirecting) is live.
initSessionExpiryInterceptor();

// Session timeout watchdog (#9769): while a tab is visible, periodically probe
// the session and kick the page to the login screen the moment it has expired,
// so an unattended tab stops displaying lead data. No-ops on pages rendered
// without a signed-in session (see the layout's data-session-authenticated).
initSessionTimeoutWatchdog();

// Degrade a failed avatar <img> (dead storage endpoint, orphaned blob, etc.)
// to the placeholder icon instead of the browser's broken-image glyph.
initAvatarFallback();
initModalAutofocus();
initTooltipEdgeAlign();
// #16878: an auto-width .gl-select* keeps its longest option's width, as a
// native select does, instead of resizing to whichever option is picked.
initSelectSteadyWidth();
// #15710: registered at boot, ahead of any Bootstrap FocusTrap, so a modal
// never pulls focus out of the GOAT Connect dialer mid-call.
installSoftphoneFocusGuard();

document.addEventListener("turbo:frame-missing", (event) => {
  const {
    detail: { response, visit },
  } = event;
  event.preventDefault();
  // A frame response with a 5xx/408 means the endpoint crashed — show the
  // global error modal and keep the user on the (still working) page instead
  // of re-visiting the broken URL.
  if (frameMissingDueToServerError(response)) return;
  visit(response.url);
});

function initializeSidebarToggle() {
  // Both main-mode and settings-mode sidebars share `.main-sidebar` and a
  // single collapsed/expanded state — apply class changes to all so the state
  // survives a sidebar-mode swap. The toggle button now lives in the layout
  // (rendered once outside both <aside>s), so getElementById returns it cleanly.
  const sidebars = document.querySelectorAll(".main-sidebar");
  const toggle = document.getElementById("sidebar-toggle");

  if (sidebars.length === 0 || !toggle) return;

  const COLLAPSED_CLASS = "collapsed-sidebar";
  const FOOTER_HIDDEN_CLASS = "sidebar-footer-hidden";
  const FOOTER_ENTERING_CLASS = "sidebar-footer-entering";
  const COLLAPSE_BREAKPOINT = 1200; // CSS breakpoint

  const addClassToAll = (...classes) =>
    sidebars.forEach((s) => s.classList.add(...classes));
  const removeClassFromAll = (...classes) =>
    sidebars.forEach((s) => s.classList.remove(...classes));
  const anyCollapsed = () =>
    Array.from(sidebars).some((s) => s.classList.contains(COLLAPSED_CLASS));

  function applySidebarState() {
    const windowWidth = window.innerWidth;
    const savedCollapsed = localStorage.getItem("sidebarCollapsed") === "true";

    if (windowWidth <= COLLAPSE_BREAKPOINT) {
      addClassToAll(COLLAPSED_CLASS, FOOTER_HIDDEN_CLASS);
    } else {
      if (savedCollapsed) {
        addClassToAll(COLLAPSED_CLASS, FOOTER_HIDDEN_CLASS);
      } else {
        removeClassFromAll(COLLAPSED_CLASS, FOOTER_HIDDEN_CLASS);
      }
    }
  }

  // Sidebar scroll position across navigations is owned exclusively by
  // sidebar_scroll_controller.js (Stimulus): it restores the outgoing position
  // and only reveals an active item hidden inside an Admin submenu group.
  // Do not add scroll-to-active logic here — a previous duplicate ran on every
  // turbo:load and yanked the nav on every click.

  function setSidebarCollapsedState(willBeCollapsed) {
    const wrapper = document.getElementById("wrapper");

    if (wrapper) {
      wrapper.classList.remove("sidebar-collapsing", "sidebar-expanding");
      wrapper.classList.add(willBeCollapsed ? "sidebar-collapsing" : "sidebar-expanding");
    }

    if (willBeCollapsed) {
      addClassToAll(FOOTER_HIDDEN_CLASS);
      addClassToAll(COLLAPSED_CLASS);
    } else {
      removeClassFromAll(COLLAPSED_CLASS);
      setTimeout(function () {
        addClassToAll(FOOTER_ENTERING_CLASS);
        removeClassFromAll(FOOTER_HIDDEN_CLASS);
        setTimeout(function () {
          removeClassFromAll(FOOTER_ENTERING_CLASS);
        }, 300);
      }, 220);
    }

    localStorage.setItem("sidebarCollapsed", willBeCollapsed);

    if (wrapper) {
      setTimeout(function () {
        wrapper.classList.remove("sidebar-collapsing", "sidebar-expanding");
      }, 300);
    }
  }

  // Toggle click handler (guard against duplicate binding)
  if (!toggle.dataset.bound) {
    toggle.dataset.bound = "true";
    toggle.addEventListener("click", function () {
      const willBeCollapsed = !anyCollapsed();
      setSidebarCollapsedState(willBeCollapsed);
    });
  }

  // Collapsed desktop: clicking empty sidebar area expands it.
  const interactiveSelector = [
    "a",
    "button",
    "input",
    "select",
    "textarea",
    "#sidebar-toggle",
    ".gl-sidebar-profile-toggle",
    ".gl-sidebar-cta-icon",
    ".gl-sidebar-link",
    ".gl-sidebar-sublink",
    ".gl-sidebar-theme-link"
  ].join(", ");

  sidebars.forEach((sidebar) => {
    if (sidebar.dataset.emptyExpandBound) return;
    sidebar.dataset.emptyExpandBound = "true";
    sidebar.addEventListener("click", function (event) {
      if (!sidebar.classList.contains(COLLAPSED_CLASS)) return;
      if (window.innerWidth <= COLLAPSE_BREAKPOINT) return;
      if (event.target.closest(interactiveSelector)) return;
      if (!event.target.closest(".gl-sidebar-scroll")) return;

      setSidebarCollapsedState(false);
    });
  });

  // On load, apply the appropriate sidebar state
  applySidebarState();

  // Listen to window resize and re-apply sidebar state as needed
  if (!window._sidebarResizeListenerBound) {
    window._sidebarResizeListenerBound = true;
    window.addEventListener("resize", () => {
      const allSidebars = document.querySelectorAll(".main-sidebar");
      if (allSidebars.length === 0) return;
      const w = window.innerWidth;
      const saved = localStorage.getItem("sidebarCollapsed") === "true";
      const shouldCollapse = w <= COLLAPSE_BREAKPOINT || saved;
      allSidebars.forEach((s) => {
        if (shouldCollapse) {
          s.classList.add(COLLAPSED_CLASS, FOOTER_HIDDEN_CLASS);
        } else {
          s.classList.remove(COLLAPSED_CLASS, FOOTER_HIDDEN_CLASS);
        }
      });
    });
  }
}

document.addEventListener("turbo:load", () => {
  initializeSidebarToggle();

  // Turnstile only initializes on a full page load, so reset widgets after Turbo navigation.
  if (window.turnstile) {
    document.querySelectorAll('.cf-turnstile').forEach((el) => {
      window.turnstile.reset(el);
    });
  }

  const wrapper = document.querySelector("#wrapper");
  const overlay = document.getElementById("loading-overlay");

  if (overlay && wrapper) {
    overlay.classList.add("hidden");
    wrapper.classList.add("loaded");
  }

  // Site banner
  const siteBanner = document.querySelector('[id^="site-banner-"]');
  if (siteBanner) {
    const bannerIdentifier = siteBanner.dataset.bannerIdentifier;

    // Set CSS custom property for banner height so fixed table wrappers can position below it
    const updateBannerHeight = () => {
      // Check if element is still in DOM before measuring
      if (!siteBanner.isConnected) return;
      const bannerHeight = siteBanner.offsetHeight;
      document.documentElement.style.setProperty('--site-banner-height', `${bannerHeight}px`);
    };

    // Defer initial measurement to ensure banner is fully rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateBannerHeight();
      });
    });

    // Re-measure on window resize (banner height may change)
    const handleResize = () => updateBannerHeight();
    window.addEventListener('resize', handleResize);

    // Cleanup resize listener on Turbo navigation to prevent memory leak
    const cleanupResizeListener = () => {
      window.removeEventListener('resize', handleResize);
    };
    document.addEventListener('turbo:before-cache', cleanupResizeListener, { once: true });

    // Direct dismiss wiring — the banner is pure gl-banner markup now, so the
    // close click is handled here instead of Bootstrap's alert JS
    // (closed.bs.alert), which needed transitional .alert classes (#9744).
    const bannerClose = siteBanner.querySelector('.gl-banner__close');
    if (bannerClose) {
      bannerClose.addEventListener('click', function () {
        siteBanner.remove();
        // The layout stamps this state on the wrapper when a banner renders
        // (_layout.scss keys the main-content offset on it) — clear it so the
        // offset goes with the banner.
        document.getElementById('page-content-wrapper')?.classList.remove('gl-layout--with-banner');
        // Reset banner height when dismissed
        document.documentElement.style.setProperty('--site-banner-height', '0px');
        // Cleanup resize listener
        cleanupResizeListener();

        post("/site_banner_dismissals", {
          body: JSON.stringify({ banner_identifier: bannerIdentifier }),
          contentType: "application/json"
        });
      }, { once: true });
    }
  } else {
    // No banner present, ensure custom property is set to 0
    document.documentElement.style.setProperty('--site-banner-height', '0px');
  }

  // Impersonation banner (#11671) — same height-publishing contract as the site
  // banner: _impersonation-banner.scss offsets #main-content and the fixed
  // full-page tables by --impersonation-banner-height. The banner is never
  // dismissible, so there is no close wiring — only measure/reset.
  const impersonationBanner = document.querySelector('.gl-impersonation-banner');
  if (impersonationBanner) {
    const updateImpersonationBannerHeight = () => {
      if (!impersonationBanner.isConnected) return;
      document.documentElement.style.setProperty(
        '--impersonation-banner-height', `${impersonationBanner.offsetHeight}px`
      );
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateImpersonationBannerHeight();
      });
    });

    const handleImpersonationResize = () => updateImpersonationBannerHeight();
    window.addEventListener('resize', handleImpersonationResize);
    document.addEventListener('turbo:before-cache', () => {
      window.removeEventListener('resize', handleImpersonationResize);
    }, { once: true });
  } else {
    document.documentElement.style.setProperty('--impersonation-banner-height', '0px');
  }

  // CRM seat notices (#14735 / #13093): publishes --crm-banner-bottom so a fixed full-page table starts below them
  // instead of painting over them (lib/crm_banner_offset.js).
  initCrmBannerOffset();
});

// Close offcanvas menus before Turbo navigates
// This ensures mobile menu closes smoothly when clicking navigation links
document.addEventListener("turbo:before-visit", () => {
  const offcanvas = document.querySelector(".offcanvas.show");
  if (offcanvas) {
    const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvas);
    if (bsOffcanvas) {
      bsOffcanvas.hide();
    }
  }
});

/////////////////////////////////////////////////////////////////////////////////
// REUSABLE FUNCTIONS

// The one canonical toast lives in lib/toast.js (unit-tested there). Exposed as
// a global so the ~234 existing callers + inline view scripts keep working.
// To use it, define a #toast-container div in the layout/page (delayMs = 0 for
// an indefinite toast).
window.showToast = showToast;

/////////////////////////////////////////////////////////////////////////////////

window.bootstrap = bootstrap;

// Calendar page load states (#14562), wired by app/views/appointments/index.html.haml.
// FullCalendar is NOT in this bundle (#19204): `loadLibrary` imports the separate
// fullcalendar_lib entry on demand and sets window.FullCalendar.
window.GoatCalendarLoadState = { bind: bindCalendarLoadState, retry: retryCalendarLoad, loadLibrary: loadFullCalendar };

